import { Component, OnDestroy, OnInit } from '@angular/core';
import { PageBreadcrumbComponent } from "../../shared/components/common/page-breadcrumb/page-breadcrumb.component";
import { Subscription } from 'rxjs';
import { SaleResponse } from '../../dto/sale.response';
import { ModalComponent } from "../../shared/components/ui/modal/modal.component";
import { BadgeComponent } from "../../shared/components/ui/badge/badge.component";
import { ButtonComponent } from "../../shared/components/ui/button/button.component";
import { DecimalPipe, NgClass } from '@angular/common';
import { SaleRegisterComponent } from './sale-register.component';
import { SaleService } from '../../services/sale.service';
import { NotificationService } from '../../shared/components/ui/notification/notification.service';

@Component({
  selector: 'app-sale',
  standalone: true,
  imports: [
    PageBreadcrumbComponent,
    BadgeComponent,
    ButtonComponent,
    DecimalPipe,
    NgClass,
    SaleRegisterComponent
  ],
  templateUrl: './sale.component.html',
  styleUrl: './sale.component.css',
})
export class SaleComponent implements OnInit, OnDestroy {

  // =========================
  // UI STATE
  // =========================
  loadingTable = false;
  showForm = false;

  // =========================
  // DATA
  // =========================
  sales: SaleResponse[] = [];
  selectedSale?: SaleResponse;

  // =========================
  // PAGINATION
  // =========================
  currentPage = 1;
  itemsPerPage = 5;
  readonly pageSizeOptions = [5, 10, 15, 20, 50];

  // =========================
  // SORT
  // =========================
  sortColumn = '';
  sortDir: 'asc' | 'desc' = 'asc';

  // =========================
  // SEARCH
  // =========================
  searchTerm = '';

  // =========================
  // TOOLTIP
  // =========================
  tooltipVisible = false;
  tooltipCode = '';
  tooltipText = '';
  tooltipX = 0;
  tooltipY = 0;

  showTooltip(event: MouseEvent, code: number | undefined, message: string | undefined): void {
      if (!message) return;
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      this.tooltipCode = code != null ? String(code) : '';
      this.tooltipText = message;
      this.tooltipX = rect.left;
      this.tooltipY = rect.top;
      this.tooltipVisible = true;
  }

  hideTooltip(): void {
      this.tooltipVisible = false;
  }

  // =========================
  // SUBSCRIPTIONS
  // =========================
  private sub = new Subscription();

  constructor(
    private saleService: SaleService,
    private notify: NotificationService
  ) { }

  // =========================
  // INIT / DESTROY
  // =========================
  ngOnInit(): void {
    this.loadSales();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  // =========================
  // LOAD SALES
  // =========================
  loadSales(): void {
    this.loadingTable = true;

    const s = this.saleService.getAll().subscribe({
      next: res => {
        this.sales = res?.data ?? [];
        this.loadingTable = false;
      },
      error: err => {
        this.sales = [];
        this.loadingTable = false;
        this.notify.error(err?.error?.message ?? 'Error al listar ventas');
      }
    });

    this.sub.add(s);
  }

  // =========================
  // CREATE / EDIT
  // =========================
  onCreateSale(): void {
    this.selectedSale = undefined;
    this.showForm = true;
  }

  onEditSale(sale: SaleResponse): void {
    this.selectedSale = sale;
    this.showForm = true;
  }

  handleSaleSaved(): void {
    this.showForm = false;
    this.selectedSale = undefined;
    this.loadSales();
  }

  onCloseForm(): void {
    this.showForm = false;
    this.selectedSale = undefined;
  }

  // =========================
  // SEARCH
  // =========================
  setSearchTerm(value: string): void {
    this.searchTerm = (value ?? '').trim().toLowerCase();
    this.currentPage = 1;
  }

  get filteredSales(): SaleResponse[] {
    let list = this.sales;
    if (this.searchTerm) {
      list = list.filter(s =>
        s.client.businessName?.toLowerCase().includes(this.searchTerm) ||
        s.client.firstName?.toLowerCase().includes(this.searchTerm) ||
        s.client.lastName?.toLowerCase().includes(this.searchTerm) ||
        s.documentSeries?.toLowerCase().includes(this.searchTerm) ||
        s.documentSequence?.toLowerCase().includes(this.searchTerm)
      );
    }
    if (this.sortColumn === 'cliente') {
      list = [...list].sort((a, b) => {
        const va = a.client.businessName || `${a.client.firstName ?? ''} ${a.client.lastName ?? ''}`.trim();
        const vb = b.client.businessName || `${b.client.firstName ?? ''} ${b.client.lastName ?? ''}`.trim();
        return this.sortDir === 'asc' ? va.localeCompare(vb, 'es') : vb.localeCompare(va, 'es');
      });
    }
    return list;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredSales.length / this.itemsPerPage));
  }

  get pageItems(): (number | '...')[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const pages = new Set<number>();
    for (let i = 1; i <= Math.min(3, total); i++) pages.add(i);
    for (let i = Math.max(1, total - 2); i <= total; i++) pages.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result: (number | '...')[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
      result.push(sorted[i]);
    }
    return result;
  }

  get currentItems(): SaleResponse[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredSales.slice(start, start + this.itemsPerPage);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  toggleSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDir = 'asc';
    }
  }

  onPageSizeChange(size: number): void {
    this.itemsPerPage = size;
    this.currentPage = 1;
  }

  // =========================
  // BADGE HELPERS
  // =========================
  getBadgeColor(status: string): 'success' | 'error' | 'warning' {
    if (status === 'COMPLETED') return 'success';
    if (status === 'DRAFT') return 'warning';
    return 'error';
  }

  // =========================
  // HELPERS
  // =========================
  retentionSymbol(sale: { currencyCode?: string; document?: { issueDate?: string } }): string {
    const d = sale.document?.issueDate;
    if (d && d >= '2026-04-23') return 'S/';
    return sale.currencyCode === 'USD' ? '$' : 'S/';
  }

  formatIssueDate(dateStr?: string): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  openPdf(url: string): void {
    window.open(url, '_blank');
  }

}
